import type { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type {
  PatientPlanValue,
  PatientPlanValues,
} from '@components/PatientPlans';

import * as ROUTES from '@constants/routes';

import { useSnackbar } from '@hooks/useSnackbar';

import type { OnboardingUser } from '@models/onboarding-member';

import {
  AccountType,
  type AccountOwnerInput,
  type CreateUserModelInput,
  type CustomPlanInput,
} from 'generated/types';
import { useRegisterAccount } from 'generated/user.generated';

import AccountInformation from './AccountInformation';
import AccountSummary from './AccountSummary';
import LicenseInformation from './LicenseInformation';
import UserFormCollection, { defaultUser } from './NewUserFormCollection';
import PatientPlans from './PatientPlans';
import { CUSTOM_LICENSE_ID } from './license-options';
import { defaultPatientPlans } from './patient-plan-defaults';
import type { AccountValues, LicenseFeatureValues } from './types';

const messages = defineMessages({
  registerFailed: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
});

const initialAccount: AccountValues = {
  name: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  country: undefined,
  postalCode: '',
  province: undefined,
};

enum Step {
  AccountInformation = 0,
  LicenseFeatures = 1,
  PatientPlans = 2,
  Members = 3,
  Summary = 4,
}

const NewAccount: React.FC<RouteComponentProps> = () => {
  const [registerUserAccount] = useRegisterAccount();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();

  const [step, setStep] = useState(0);
  const [members, setMembers] = useState<OnboardingUser[]>([
    { ...defaultUser },
  ]);
  const [account, setAccount] = useState<AccountValues>({
    ...initialAccount,
  });
  const [licenseFeatures, setLicenseFeatures] = useState<LicenseFeatureValues>({
    // Custom is preselected because it is the only licence the first iteration
    // of the epic offers, so there is nothing for the admin to choose between.
    license_id: CUSTOM_LICENSE_ID,
    limit_patients: 0,
    org_feature_ids: [],
    is_patient_mfa_required: undefined,
    is_practitioner_mfa_required: undefined,
    medly_enabled: undefined,
  });
  const [patientPlans, setPatientPlans] = useState<PatientPlanValue[]>(
    defaultPatientPlans()
  );

  function handleSubmitAccountInfo(values: AccountValues) {
    setAccount(values);
    setStep(Step.AccountInformation + 1);
  }

  function handleSubmitMembers(values: { members: OnboardingUser[] }) {
    setMembers(values.members);
    setStep(Step.Members + 1);
  }

  function handleSubmitLicenseFeatures(values: LicenseFeatureValues) {
    setLicenseFeatures(values);
    setStep(Step.LicenseFeatures + 1);
  }

  function handleSubmitPatientPlans(values: PatientPlanValues) {
    setPatientPlans(values.plans);
    setStep(Step.PatientPlans + 1);
  }

  /** Keep the plans drafted so far when stepping back to the licence step. */
  function handleBackFromPatientPlans(values: PatientPlanValues) {
    setPatientPlans(values.plans);
    handleBack();
  }

  async function handleSubmit() {
    function mapUser(user: OnboardingUser) {
      const userInput: CreateUserModelInput = {
        email: user.email.toLowerCase(),
        first_name: user.firstName,
        middle_name: user.middleName,
        last_name: user.lastName,
        toc_completed: user.consents?.terms,
        pn_completed: user.consents?.terms,
        casl_completed: user.consents?.contact,
        roles: user.roles.map((r) => r.label),
        // date_of_birth: user.dateOfBirth
        //   ? dayjs(user.dateOfBirth).format('YYYY-MM-DD')
        //   : null,
      };
      return userInput;
    }

    const primaryUser = mapUser(members[0]) as AccountOwnerInput;
    const secondaryUsers: CreateUserModelInput[] = members
      .slice(1)
      .map(mapUser);
    const {
      license_id,
      limit_patients,
      org_feature_ids,
      is_patient_mfa_required,
      is_practitioner_mfa_required,
      medly_enabled,
    } = licenseFeatures;

    // `local_id` is wizard-only bookkeeping, and array order is what carries
    // each plan's display order: CustomPlanInput has no display_order field.
    const patient_plans: CustomPlanInput[] = patientPlans.map(
      ({ name, description, seats, feature_ids }) => ({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        seats: Number(seats),
        feature_ids,
      })
    );

    let registerAccountResponse;
    try {
      registerAccountResponse = await registerUserAccount({
        variables: {
          input: {
            owner: primaryUser,
            members: secondaryUsers,
            currency: account.currency,
            account_name: account.name,
            phone_number: account.phone,
            billing_address: account.line1,
            billing_address2: account.line2,
            city: account.city,
            country_id: account.country,
            region_id: account.province,
            zip_postal: account.postalCode,
            account_type: AccountType.organization,
            // Licence, patient seat pool and org feature set are all applied by
            // registerAccount in one transaction (VEG-702).
            settings: {
              // "Custom" is the absence of a shared tier rather than a licence
              // of its own: sending no license_id is what makes registerAccount
              // mint the org one, starting with nothing enabled (VEG-712).
              ...(license_id === CUSTOM_LICENSE_ID ? {} : { license_id }),
              limit_patients,
              ...(is_patient_mfa_required !== undefined && {
                is_patient_mfa_required,
              }),
              ...(is_practitioner_mfa_required !== undefined && {
                is_practitioner_mfa_required,
              }),
              ...(medly_enabled !== undefined && {
                medly_enabled,
              }),
            },
            org_feature_ids,
            // Creating an org with no patient plans is valid; send no key at
            // all rather than an empty list, as with license_id above.
            ...(patient_plans.length ? { patient_plans } : {}),
          },
        },
      });
    } catch {
      // Apollo's default errorPolicy rejects on a GraphQL error, so without
      // this the rejection escapes handleSubmit entirely and the caller is
      // left waiting on a promise that never resolves.
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.registerFailed),
      });
      return;
    }

    const accountId =
      registerAccountResponse.data?.registerAccount.account.account_id;

    if (!accountId) {
      // `errors` can be an empty array: optional chaining guards `errors` being
      // nullish, not `errors[0]` being undefined.
      const reason = registerAccountResponse.errors?.[0]?.message.replace(
        /^(owner\.|members\.)/,
        ''
      );
      enqueueSnackbar({
        variant: 'error',
        message: reason || formatMessage(messages.registerFailed),
      });
      return;
    }

    // No follow-up updateAccountLicense here: registerAccount gives the org its
    // own licence to hold its feature set, and re-pointing the account setting
    // back at a shared template would discard it.
    navigate(ROUTES.ADMIN_DASHBOARD);
  }

  function handleBack() {
    setStep((step) => step - 1);
  }

  return (
    <>
      {step === Step.AccountInformation && (
        <AccountInformation
          initialValues={account}
          onSubmit={handleSubmitAccountInfo}
        />
      )}
      {step === Step.LicenseFeatures && (
        <LicenseInformation
          initialValues={licenseFeatures}
          onBack={handleBack}
          onSubmit={handleSubmitLicenseFeatures}
        />
      )}
      {step === Step.PatientPlans && (
        <PatientPlans
          initialValues={{ plans: patientPlans }}
          limitPatients={licenseFeatures.limit_patients}
          orgFeatureIds={licenseFeatures.org_feature_ids}
          onBack={handleBackFromPatientPlans}
          onSubmit={handleSubmitPatientPlans}
        />
      )}
      {step === Step.Members && (
        <UserFormCollection
          initialMembers={members}
          onSubmit={handleSubmitMembers}
          onBack={handleBack}
          accountType={AccountType.organization}
        />
      )}
      {step === Step.Summary && (
        <AccountSummary
          account={account}
          members={members}
          onBack={handleBack}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default NewAccount;
